/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { createLinkControl } from '@veeam/components';
import { BackupServicesType, TimeUnit } from '@veeam-vspc/models/web-controllers';
import { useGlobalLang } from '@veeam-vspc/shared/components';
import { useLocation } from 'react-router-dom';

import type { VspcLang } from 'configs/languages';

import { AgentBackups } from './components/AgentBackups';
import { ConsumedStorage } from './components/ConsumedStorage';
import { FilterContext } from '../../contexts';
import { FilterStore } from '../../stores';
import { SobrUsage } from './components/SobrUsage';
import { StatefulCombobox } from 'views/components/StatefulCombobox';
import { VmBackups } from './components/VmBackups';
import { VmReplicas } from './components/VmReplicas';
import { WidgetColumnsStyled, WidgetViewStyled, WidgetRowsStyled } from 'components/layouts/WidgetView';
import { useFilterStore } from '../../hooks';
import { useLocalStorageValue } from 'views/hooks';

import type { ComboboxItem } from 'core/interfaces';

interface CloudConnectLayoutProps {
    periodStateId: string;
}

const CloudConnectLayout: React.FC<CloudConnectLayoutProps> = observer((props) => {
    const { periodStateId } = props;
    const filterStore = useFilterStore();
    const lang = useGlobalLang<VspcLang>();

    return (
        <WidgetViewStyled>
            <StatefulCombobox
                controlRenderer={createLinkControl()}
                label={`${lang.REPORTING_PERIOD}:`}
                onChange={filterStore.setReportingPeriod}
                stateId={periodStateId}
                textGetter={(p: ComboboxItem) => p.text}
                value={filterStore.reportingPeriod}
                valueGetter={(p: ComboboxItem) => p.id}
                data={[
                    { id: TimeUnit.Weeks, text: lang.WEEKLY },
                    { id: TimeUnit.Months, text: lang.MONTHLY },
                ]}
            />

            <WidgetColumnsStyled>
                <WidgetRowsStyled>
                    <VmBackups />
                    <AgentBackups />
                    <SobrUsage />
                </WidgetRowsStyled>

                <WidgetRowsStyled>
                    <VmReplicas />
                    <ConsumedStorage />
                </WidgetRowsStyled>
            </WidgetColumnsStyled>
        </WidgetViewStyled>
    );
});

export interface CloudConnectProps {
    stateId: string;
}

export const CloudConnect: React.FC<CloudConnectProps> = () => {
    const { pathname } = useLocation();
    const periodStateId = `${pathname}.ReportingPeriod`;
    const period = useLocalStorageValue<TimeUnit>(periodStateId, TimeUnit.Months);

    const filterStore = useMemo(() => new FilterStore(period, BackupServicesType.All), [period]);

    return (
        <FilterContext.Provider value={filterStore}>
            <CloudConnectLayout periodStateId={periodStateId} />
        </FilterContext.Provider>
    );
};
